import {
	Component,
	Input,
	Output,
	EventEmitter,
	OnInit,
	OnDestroy,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	forwardRef,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { Subject, takeUntil } from "rxjs";

// Models
import { PagoElectricidadViewModel } from "src/app/_core/models/centros/cebe-contract-list-base.model";

// Services
import { MasterService } from "src/app/_core/providers/master.service";
import { NotificationService } from "src/app/_core/providers/notification.service";

@Component({
    selector: "app-pago-electricidad-autocomplete",
    templateUrl: "./pago-electricidad-autocomplete.component.html",
    styleUrls: ["./pago-electricidad-autocomplete.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PagoElectricidadAutocompleteComponent),
            multi: true,
        },
    ],
    standalone: false
})
export class PagoElectricidadAutocompleteComponent
	implements OnInit, OnDestroy, ControlValueAccessor
{
	private readonly destroy$ = new Subject<void>();

	// Inputs
	@Input() label = "Pago de Electricidad";
	@Input() placeholder =
		"Buscar pago de electricidad por código o descripción";
	@Input() required = false;
	@Input() disabled = false;
	@Input() hint = "Busque por código o descripción de pago de electricidad";

	// Outputs
	@Output() pagoElectricidadSelected =
		new EventEmitter<PagoElectricidadViewModel>();

	// State
	pagosElectricidad: PagoElectricidadViewModel[] = [];
	pagoElectricidadId: number | null = null;
	selectedPagoElectricidad: PagoElectricidadViewModel | null = null;

	// ControlValueAccessor
	private onChange = (_value: number | null): void => {
		// This is intentional
	};
	private onTouched = (): void => {
		// This is intentional
	};

	constructor(
		private readonly masterService: MasterService,
		private readonly notificationService: NotificationService,
		private readonly cdr: ChangeDetectorRef,
	) {}

	ngOnInit(): void {
		this.loadPagosElectricidad();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	// ControlValueAccessor implementation
	writeValue(value: number | null): void {
		this.pagoElectricidadId = value ?? null;
		this.syncSelected();
		this.cdr.markForCheck();
	}

	registerOnChange(fn: (value: number | null) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
		this.cdr.markForCheck();
	}

	/**
	 * Carga la lista de pagos de electricidad
	 */
	private loadPagosElectricidad(): void {
		this.masterService
			.getPagoElectricidadList()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (pagosElectricidad) => {
					this.pagosElectricidad = pagosElectricidad;
					this.syncSelected();
					this.cdr.markForCheck();
				},
				error: () => {
					this.notificationService.showError(
						"Error al cargar los pagos de electricidad",
					);
				},
			});
	}

	/** Recalcula el objeto seleccionado a partir del id actual. */
	private syncSelected(): void {
		this.selectedPagoElectricidad =
			this.pagoElectricidadId != null
				? (this.pagosElectricidad.find(
						(p) => p.id === this.pagoElectricidadId,
					) ?? null)
				: null;
	}

	/**
	 * Obtiene el texto mostrado para un pago de electricidad
	 */
	displayPagoElectricidad = (
		pagoElectricidad: PagoElectricidadViewModel | string,
	): string => {
		if (typeof pagoElectricidad === "string") {
			return pagoElectricidad;
		}
		return pagoElectricidad &&
			pagoElectricidad.codigo &&
			pagoElectricidad.descripcion
			? `${pagoElectricidad.codigo} - ${pagoElectricidad.descripcion}`
			: "";
	};

	/** Selección desde el autocomplete `.ds`. */
	onValueChange(pagoElectricidad: PagoElectricidadViewModel | null): void {
		this.pagoElectricidadId = pagoElectricidad ? pagoElectricidad.id : null;
		this.selectedPagoElectricidad = pagoElectricidad;
		this.onChange(this.pagoElectricidadId);
		this.onTouched();
		if (pagoElectricidad) {
			this.pagoElectricidadSelected.emit(pagoElectricidad);
		}
		this.cdr.markForCheck();
	}

	onControlTouched(): void {
		this.onTouched();
	}
}
